import React, { useEffect, useMemo } from 'react';
import { ResizeAspectLock, SetTempElementSize } from './resizeTypes';
import { updateElementResizingValues } from './store/resizingActions';
import { useDispatch } from 'react-redux';
import { animatePointTransition } from '../../utils/animation/animationUtils';
import { PointYOptional } from '../../../common/maths/geometry/pointTypes';
import useAutoCancellingAnimationFrame from '../../utils/react/useAutoCancellingAnimationFrame';
import { ResizeProps, ResizeState } from './useGroupResizeBehaviour';
import resizeAnimationSingleton from './ResizeAnimationSingleton';

type UseElementResizeHandlersParams = {
    props: ResizeProps;
    finishResize: () => void;
    setTempElementSize: SetTempElementSize;
    setTempPosition: (position: PointYOptional) => void;
    repositionOnly: boolean;
    resizeStateRef: React.MutableRefObject<ResizeState>;
};

/**
 * Registers resizing values like min scale and animation callback for an individual resizing element for use in group resizing.
 */
const useElementResizeHandlers = ({
    props,
    resizeStateRef,
    finishResize,
    setTempElementSize,
    setTempPosition,
    repositionOnly,
}: UseElementResizeHandlersParams) => {
    const {
        elementId,
        gridSize,
        isResizing,
        resizeAspectLock,
        minWidth,
        minHeight,
        getNonMediaHeight = () => 0,
    } = props;

    const dispatch = useDispatch();

    const resizeAnimationFrame = useAutoCancellingAnimationFrame();
    const positionAnimationFrame = useAutoCancellingAnimationFrame();

    const animateSnapToGrid = () => {
        const { tempSize, tempPosition, isResizingOrAnimating } = resizeStateRef.current;

        if (!isResizingOrAnimating) return;

        const size = resizeStateRef.current.animationTargetSize;
        const position = tempPosition ? { x: Math.round(tempPosition.x), y: Math.round(tempPosition.y) } : null;

        if (!tempSize && !size && !tempPosition && !position) {
            finishResize();
            return;
        }

        if (tempSize && size) {
            animatePointTransition(
                { x: tempSize.width, y: tempSize.height },
                { x: size.width, y: size.height },
                ({ x, y }: PointYOptional) => y && setTempElementSize({ width: x, height: y }),
                resizeAnimationFrame,
                finishResize,
            );
        }

        if (tempPosition && position) {
            // Ensure the finish function runs once, either after the size animation or after the position animation
            const onComplete = tempSize && size ? undefined : finishResize;

            animatePointTransition(tempPosition, position, setTempPosition, positionAnimationFrame, onComplete);
        }
    };

    // Important to calculate this as resizing starts, as we may need it later for recalculating min scale if resizeAspectLock changes
    const initialNonMediaHeight = useMemo(getNonMediaHeight, [isResizing]);

    const getElementMinResizeScales = () => {
        if (repositionOnly) {
            return { x: 0, y: 0 };
        }

        const initialElementSize = resizeStateRef.current?.initialElementSize || { width: 0, height: 0 };
        const minScaleX = minWidth / initialElementSize.width;
        const minScaleY = minHeight / initialElementSize.height;

        if (resizeAspectLock === ResizeAspectLock.MEDIA) {
            // When locking to media aspect ratio, media will not resize to account for changes to non-media content,
            // so we need to calculate the min scale based on the core content that will always remain at lower widths
            // In other aspect lock modes this is handled by useNonMediaContentResize.ts
            const initialCoreContentHeight = initialElementSize.height - initialNonMediaHeight / gridSize;
            const minScaleY = minHeight / initialCoreContentHeight;
            const fixedAspectRatioMinScale = Math.max(minScaleX, minScaleY);
            return { x: fixedAspectRatioMinScale, y: fixedAspectRatioMinScale };
        }

        return { x: minScaleX, y: minScaleY };
    };

    useEffect(() => {
        if (!isResizing) return;

        resizeAnimationSingleton.registerCallback(elementId, animateSnapToGrid);

        dispatch(
            updateElementResizingValues(elementId, {
                minResizeScale: getElementMinResizeScales(),
                lockAspectRatio: resizeAspectLock !== ResizeAspectLock.NONE,
            }),
        );
    }, [isResizing, resizeAspectLock]);
};

export default useElementResizeHandlers;
